// useShiplistFilters.tsx
import { useMemo, useState } from 'react';
import { ShipList } from '@ais-ui/api-spec/config';
import { SimpleFilter, useFilterContext } from '../../Map/FilterContext';
import { useSettings } from '../../Settings/SettingsProvider';
import { ShiplistFilters, ShiplistSortConfig } from './types';

const isShiplistFilterApplied = (
  shiplist: ShipList,
  selectedFilters: SimpleFilter[],
): boolean => {
  return selectedFilters.some(
    (selectedFilter) => selectedFilter.id === shiplist.id,
  );
};

const isShiplistColorApplied = (shiplist: ShipList, settings: any): boolean => {
  const shownShiplistsId =
    settings?.['ribbonPlugin.settings']?.legend_shiplists || [];
  return shownShiplistsId.includes(shiplist.id);
};

export const useShiplistFilters = (
  shiplists: ShipList[],
  searchTerm: string,
) => {
  const { settings } = useSettings();
  const { simpleFilters } = useFilterContext();

  const [shiplistFilters, setShiplistFilters] = useState<ShiplistFilters>({
    owner: 'all',
    appliedAsFilter: 'all',
    colorBy: 'all',
  });

  const [sortConfig, setSortConfig] = useState<ShiplistSortConfig>({
    type: 'name',
    direction: 'asc',
  });

  const filteredAndSortedShiplists = useMemo(() => {
    if (!shiplists) return [];

    // First apply search filter
    let result = [...shiplists];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(
        (shiplist) =>
          shiplist.name?.toLowerCase().includes(searchLower) ||
          shiplist.owner?.toLowerCase().includes(searchLower),
      );
    }

    // Apply filters
    result = result.filter((shiplist) => {
      // Filter by owner
      if (shiplistFilters.owner !== 'all') {
        if (shiplist.owner !== shiplistFilters.owner) return false;
      }

      // Filter by applied as filter status
      if (shiplistFilters.appliedAsFilter !== 'all') {
        const isApplied = isShiplistFilterApplied(shiplist, simpleFilters);
        if (shiplistFilters.appliedAsFilter === 'applied' && !isApplied)
          return false;
        if (shiplistFilters.appliedAsFilter === 'not-applied' && isApplied)
          return false;
      }

      // Filter by color status
      if (shiplistFilters.colorBy !== 'all') {
        const isColored = isShiplistColorApplied(shiplist, settings);
        if (shiplistFilters.colorBy === 'colored' && !isColored) return false;
        if (shiplistFilters.colorBy === 'not-colored' && isColored)
          return false;
      }

      return true;
    });

    // Apply sorting (only by name)
    result.sort((a, b) => {
      const nameA = a.name || '';
      const nameB = b.name || '';
      const comparison = nameA.localeCompare(nameB);
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [
    shiplists,
    searchTerm,
    shiplistFilters,
    sortConfig,
    settings,
    simpleFilters,
  ]);

  return {
    filteredAndSortedShiplists,
    shiplistFilters,
    setShiplistFilters,
    sortConfig,
    setSortConfig,
  };
};
